import mean from 'lodash/mean';
import zip from 'lodash/zip';
import moment from 'moment';

import {SEGMENT_PREFIX, TableRow} from 'common/components/data-table/types';
import {ColumnGroup} from 'common/components/data-table/types';
import {TableColumn} from 'common/components/data-table/types';
import {abbreviateNumber, commaizeNumber} from 'common/util';
import {formatDate, UNITS} from 'common/util/date';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

import {TrendDataTableRow, TrendDataTableSegment, ValueFormatterFunc} from './types';

export const LineDataTableFields = {
  Average: `averageValue`,
  TimeComparisonAverage: `tcAverageValue`,
  DatePointPrefix: `datePoint-`,
  TimeComparisonDatePointPrefix: `tcDatePoint-`,
};

// TODO(mack): Add unit tests for this
export function getDataTableRows(rows: TrendDataTableRow[]): TableRow[] {
  const hasTimeComparison = Boolean(rows[0]?.timeComparisonData);

  return rows.map(
    (segmentData): TableRow => {
      const allDateValueMap = Object.fromEntries([
        ...segmentData.data.map(({timestamp, value}) => [`${LineDataTableFields.DatePointPrefix}${timestamp}`, value]),
        ...(hasTimeComparison
          ? segmentData.timeComparisonData.map(({timestamp, value}) => [
              `${LineDataTableFields.TimeComparisonDatePointPrefix}${timestamp}`,
              value,
            ])
          : []),
      ]);

      const segmentsMap = Object.fromEntries(
        segmentData.segmentValues.map((segmentValue, i) => [`${SEGMENT_PREFIX}${i}`, segmentValue]),
      );

      return {
        identifier: segmentData.rowId,
        checkedColor: segmentData.selectionColor,
        isSelected: segmentData.isSelected,
        ...segmentsMap,
        [LineDataTableFields.Average]: mean(segmentData.data.map(({value}) => value)),
        ...(hasTimeComparison
          ? {
              [LineDataTableFields.TimeComparisonAverage]: mean(segmentData.timeComparisonData.map(({value}) => value)),
            }
          : {}),
        ...allDateValueMap,
      };
    },
  );
}

export function formatDataTableNumber(value: number, {isAverage}: {isAverage: boolean}) {
  return value < 1000000
    ? // For average column, show 1 decimal place even if the value is an integer.
      commaizeNumber(value, {maxPrecision: 1, exactPrecision: isAverage})
    : abbreviateNumber(value, {precision: 1});
}

const HEADER_CUSTOM_DATE_FORMATTING = {
  [UNITS.hour]: `ha, MMM D`,
  [UNITS.day]: `MMM D`,
  [UNITS.week]: `MMM D`,
  [UNITS.month]: `MMM YYYY`,
  [UNITS.quarter]: `[Q]Q YYYY`,
};

function formatWeekRange(mDate) {
  const mToDate = moment(mDate).add(6, `days`);
  const format = HEADER_CUSTOM_DATE_FORMATTING[UNITS.week];
  return `${mDate.format(format)} - ${mToDate.format(format)}`;
}

// TODO(mack): Add unit tests for this
export function getDataTableColumnGroups({
  segments,
  rows,
  trendUnit,
  valueFormatter = formatDataTableNumber,
}: {
  segments: TrendDataTableSegment[];
  rows: TrendDataTableRow[];
  trendUnit: TimeUnit;
  valueFormatter?: ValueFormatterFunc;
}): ColumnGroup[] {
  const hasTimeComparison = Boolean(rows[0]?.timeComparisonData);

  // TODO(mack): Implement special sorting for numeric properties. Can copy from Funnels:
  // https://github.com/mixpanel/analytics/blob/bd190f39528596f4695e91995169417952a09fa6/iron/common/widgets/funnels-table/utils.ts#L170

  const CommonSegmentColumnFields = {
    isSortable: true,
    sticky: true,
    type: `text` as const,
  };
  const segmentColumnGroups = segments.map((segment, i) => {
    const column = {
      ...CommonSegmentColumnFields,
      title: segment.title,
      field: `${SEGMENT_PREFIX}${i}`,
      width: segment.width || 100,
    };
    return {columns: [column]};
  });

  const CommonAverageColumnFields = {
    isSortable: true,
    sticky: true,
    type: `number` as const,
    width: 110,
    ...(valueFormatter
      ? {cellRenderer: ({item, context}) => valueFormatter(item[context.field], {isAverage: true})}
      : {}),
  };
  const averageColumns: TableColumn[] = [
    {
      ...CommonAverageColumnFields,
      title: `Average`,
      field: LineDataTableFields.Average,
    },
    ...(hasTimeComparison
      ? [
          {
            ...CommonAverageColumnFields,
            title: `Past Avg.`,
            field: LineDataTableFields.TimeComparisonAverage,
          },
        ]
      : []),
  ];

  const allDatePoints = zip(rows[0].data, rows[0].timeComparisonData);

  const CommonDateColumnFields = {
    isSortable: true,
    type: `number` as const,
    width: 100,
    ...(valueFormatter
      ? {cellRenderer: ({item, context}) => valueFormatter(item[context.field], {isAverage: false})}
      : {}),
  };
  const datePointColumnGroups: ColumnGroup[] = allDatePoints.map(
    ([datePoint, timeComparisonDatePoint]): ColumnGroup => {
      const columns: TableColumn[] = [
        {
          ...CommonDateColumnFields,
          title: formatDate(datePoint.timestamp, {
            unit: trendUnit,
            customFormatting: HEADER_CUSTOM_DATE_FORMATTING,
            weekRangeFormatter: formatWeekRange,
          }),
          tooltip: formatDate(datePoint.timestamp, {unit: trendUnit}),
          field: `${LineDataTableFields.DatePointPrefix}${datePoint.timestamp}`,
        },
      ];
      if (hasTimeComparison) {
        columns.push({
          ...CommonDateColumnFields,
          title: `${formatDate(timeComparisonDatePoint.timestamp, {
            unit: trendUnit,
            customFormatting: HEADER_CUSTOM_DATE_FORMATTING,
            weekRangeFormatter: formatWeekRange,
          })} (Past)`,
          tooltip: `${formatDate(timeComparisonDatePoint.timestamp, {unit: trendUnit})} (Past)`,
          field: `${LineDataTableFields.TimeComparisonDatePointPrefix}${timeComparisonDatePoint.timestamp}`,
        });
      }
      return {columns};
    },
  );

  return [...segmentColumnGroups, {columns: averageColumns}, ...datePointColumnGroups];
}
